import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ChatMessage, ThreadChatState } from 'models/ai-chat/chat.types';
import { FeedbackRequest, QueryResponse } from 'models/ai-chat/ai-chat-response';

const initialState: ThreadChatState = {
  messages: [],
  pendingMessage: false,
  error: null,
  currentMessageId: null,
  suggestions: [],
  suggestedVisualizations: [],
  suggestionText: null,
  suggestedQuestions: null,
  loading: false
};

const threadChatSlice = createSlice({
  name: 'threadChat',
  initialState,
  reducers: {
    // Existing reducers
    setCurrentThread: (state, action: PayloadAction<string | null>) => {
      state.currentMessageId = action.payload;
      state.messages = [];
      state.pendingMessage = false;
      state.error = null;
      state.loading = true;
    },
    addMessage: (state, action: PayloadAction<ChatMessage>) => {
      state.messages.push(action.payload);
    },
    clearMessages: (state) => {
      state.messages = [];
    },
    setSuggestions: (state, action: PayloadAction<string[]>) => {
      state.suggestions = action.payload;
    },

    setPendingMessage: (state, action: PayloadAction<{ prompt: string }>) => {
      state.pendingMessage = true;
      state.error = null;
      
      state.messages.push({
        role: 'user',
        content: action.payload.prompt,
        created_at: new Date().toISOString(),
        message_id: `temp-${Date.now()}`,
        sql_query: '',
        feedback_type: null,
        feedback_value: null,
        metadata: null
      });
      
      // Add pending assistant message
      state.messages.push({
        role: 'assistant',
        content: '',
        created_at: new Date().toISOString(),
        message_id: `pending-${Date.now()}`,
        pending: true,
        sql_query:'',
        feedback_type: null,
        feedback_value: null,
        metadata: null
      });
    },

    setMessageSuccess: (state, action: PayloadAction<QueryResponse>) => {
      state.pendingMessage = false;
      
      // Remove pending messages
      state.messages = state.messages.filter((msg:any) => !msg.pending);

      const response = action.payload;

      // Update conversation ID if not set
      if (!state.currentMessageId) {
        state.currentMessageId = response.message_id; 
      }

      if (response.error) {
        state.error = response.error;
        state.messages.push({
          role: 'assistant',
          content: `Please contact AJ for further assistance`,
          created_at: new Date().toISOString(),
          message_id: response.message_id,
          sql_query: response.sql_query,
          visualization: {
            type: 'text',
            data: null
          },
          error: true,
          feedback_type: null,
          feedback_value: null,
          metadata:response.metadata
        });
        state.suggestions = [];
        state.suggestedQuestions = [];
        state.suggestedVisualizations = [];
      } else {
        // Add assistant message
        state.messages.push({
          role: 'assistant',
          content: response.text,
          created_at: new Date().toISOString(),
          message_id: response.message_id,
          sql_query: response.sql_query,
          pending:false,
          visualization: {
            type: response.visualization,
            data: response.data,
          },
          feedback_type: null,
          feedback_value: null,
          metadata:response.metadata
        });
        
        // Process suggestions
        if (response.suggestiontext) {
          const suggestions = response.suggestiontext
            .split('?')
            .map((s:any) => s.trim())
            .filter((s:any) => s.length > 0)
            .map((s:any) => s + '?');
          
          state.suggestions = suggestions;
        }

        // Process suggested questions
        if (response.suggested_questions && response.suggested_questions.length > 0) {
          state.suggestedQuestions = state.suggestions.concat(response.suggested_questions);
        }

        // Process suggested visualizations
        if (response.suggested_visualizations && response.suggested_visualizations.length > 0) {
          state.suggestedVisualizations = response.suggested_visualizations;
        }
      }
    },


    loadExistingMessageSuccess: (state, action: PayloadAction<QueryResponse[]>) => {
      state.pendingMessage = false;
      state.messages = state.messages.filter((msg:any) => !msg.pending);
      const response = action.payload;

      if(response!=null && response.length > 0){
        response.forEach(element => {
          state.messages.push({
            role: (element.role == 'user') ? 'user' : 'assistant',
            content: element.text,
            created_at: new Date().toISOString(),
            message_id: element.message_id,
            sql_query: element.sql_query,
            visualization: {
              type: element.visualization,
              data: (element.data!=null) ? JSON.parse(element.data): null,
            },
            feedback_type: (element.feedback?.type) ? element.feedback?.type : null,
            feedback_value: (element.feedback?.value) ? element.feedback?.value : null,
            metadata :element.metadata
          });
        });
      }
      state.loading = false;
    },

    setMessageFailure: (state, action: PayloadAction<string>) => {
      state.pendingMessage = false;
      state.error = action.payload || 'Failed to send message';
      state.loading = false;
      // Remove pending messages
      state.messages = state.messages.filter((msg:any) => !msg.pending);

      state.messages.push({
        role: 'assistant',
        content: `Error: ${state.error}`,
        created_at: new Date().toISOString(),
        message_id: `error-${Date.now()}`,
        error: true,
        sql_query: '',
        feedback_type: null,
        feedback_value: null,
        metadata: null
      });
    },

    addMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id, feedback_type, feedback_value } = action.payload;
      
      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === message_id
      );

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: feedback_type,
          feedback_value: feedback_value
        };
      }
    },

    removeMessageFeedback: (state, action: PayloadAction<FeedbackRequest>) => {
      const { message_id } = action.payload;
      
      const messageIndex = state.messages.findIndex(
        msg => msg.message_id === message_id
      );

      if (messageIndex !== -1) {
        state.messages[messageIndex] = {
          ...state.messages[messageIndex],
          feedback_type: null,
          feedback_value: null
        };
      }
    }
  }
});

export const { 
  setCurrentThread, 
  addMessage, 
  clearMessages, 
  setSuggestions,
  setPendingMessage,
  setMessageSuccess,
  loadExistingMessageSuccess,
  setMessageFailure,
  addMessageFeedback,
  removeMessageFeedback
} = threadChatSlice.actions;
export default threadChatSlice.reducer;